import { AbstractControl } from '@angular/forms';
import {
  IFormFieldValidation,
  ValidationType,
} from '@nibtravel/shared/util/models';

import { BaseInputComponent } from '../../../common/base-input.component';

/**
 * A convenience class to help consume the validators in an easier way.
 * The default implementation can be extended/overridden on the caller.
 *
 * It contains all default messages.
 *
 * NOTE: NEED TO FIND THE CORRECT MESSAGES. THIS IS CURRENTLY JUST A PLACEHOLDER FOR THE MESSAGES
 */
export class ValidationsHelper {
  /**
   * The default implementation of min validator.
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly Min: IFormFieldValidation = {
    type: ValidationType.Min,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      const fieldMsg = fieldName ? `for element ${fieldName}` : '';
      return `Please enter a value greater than ${error.min} ${fieldMsg}.`;
    },
  };

  /**
   * The default implementation of max validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly Max: IFormFieldValidation = {
    type: ValidationType.Max,
    parameter: 65,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      const fieldMsg = fieldName ? `for element ${fieldName}` : '';
      return `Please enter a value less than ${error.max} ${fieldMsg}.`;
    },
  };

  /**
   * The default implementation of minLength validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly MinLength: IFormFieldValidation = {
    type: ValidationType.MinLength,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      return `Field ${fieldName} requires mininum length of ${error.min}.`;
    },
  };

  /**
   * The default implementation of maxLength validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly MaxLength: IFormFieldValidation = {
    type: ValidationType.MaxLength,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      return `Field ${fieldName} requires maximum length of ${error.max}.`;
    },
  };

  /**
   * The default implementation of required validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly Required: IFormFieldValidation = {
    type: ValidationType.Required,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      return `Please enter the ${fieldName?.toLocaleLowerCase()}.`;
    },
  };

  /**
   * The default implementation of requiredTrue validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly RequiredTrue: IFormFieldValidation = {
    type: ValidationType.RequiredTrue,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent
    ) => {
      const fieldName = getFieldName(host);
      return `Please enter a value for ${fieldName}.`;
    },
  };

  /**
   * The default implementation of email validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly Email: IFormFieldValidation = {
    type: ValidationType.Email,
    message: () => `Email has incorrect format.`,
  };

  /**
   * The default implementation of pattern validator
   *
   * @static
   * @type {IFormFieldValidation}
   * @memberof DefaultValidators
   */
  static readonly Pattern: IFormFieldValidation = {
    type: ValidationType.Pattern,
    message: (
      control: AbstractControl,
      error: any,
      host: BaseInputComponent,
      validation: IFormFieldValidation
    ) =>
      `Field contains invalid characters. Allowed characters consist in ${validation.parameter}.`,
  };
}

export function getFieldName(host: BaseInputComponent): string | null {
  let fieldName = null;
  if (host) {
    fieldName = host.validationlabel || host.label;
  }

  return fieldName;
}
